import React, { useEffect, useState } from 'react';
import { useDataProvider, useRecordContext, useTranslate, FunctionField } from 'react-admin';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// DataProviderの型定義
interface MyDataProvider {
  getOrderDetails: (resource: string, params: { id: string }) => Promise<{ data: any[] }>;
}

const formatBaseRate = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return '-';
  }
  if (num === -9999) {
    return '-';
  }
  return num.toLocaleString() + '%';
};

const formatIsExclude = (value: boolean | null | undefined): string => {
  if (value === true) {
    return '○';
  }
  return '×';
};

interface OrderDetailExpandProps {
  hideParentReward?: boolean;
}

/**
 * 注文詳細をAPIから取得して展開表示するためのコンポーネント
 */
const OrderDetailExpand: React.FC<OrderDetailExpandProps> = ({ hideParentReward = false }) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const dataProvider = useDataProvider<any>();

  // MUIのブレークポイントを使用してSPサイズかどうかを判定
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const [details, setDetails] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!record) {
        setLoading(false);
        return;
      }
      try {
        const response = await dataProvider.getOrderDetails('orders/details', { id: record.id });
        setDetails(response.data);
      } catch (err) {
        setError(err);
        console.error('Failed to fetch order details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [record, dataProvider]);

  let content: React.ReactNode; // 表示する中身を一時的に保持する変数

  if (loading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (error) {
    content = (
      <Box>
        <Typography color="error">詳細の取得に失敗しました。</Typography>
      </Box>
    );
  } else if (!details || details.length === 0) {
    content = (
      <Box>
        <Typography>商品明細はありません。</Typography>
      </Box>
    );
  } else {
    // 親パートナー名の取得
    const parentPartnerName = details && details.length > 0 ? details[0].parentPartnerName : null;

    // 1次パートナーが存在する注文かどうかを判定
    const orderHasIntroducer = details.some((item: any) => {
      const introducerPrice = item.introducerRewardPrice !== null ? parseFloat(item.introducerRewardPrice) : 0;
      const isBaseIntroducerRewardNull = item.baseIntroducerReward === null || item.baseIntroducerReward === undefined || item.baseIntroducerReward === '';

      // introducerRewardPrice = 0 かつ baseIntroducerReward が null の場合は紹介者報酬は非表示
      if (introducerPrice === 0 && isBaseIntroducerRewardNull) {
        return false;
      }

      return (
        introducerPrice > 0 ||
        (item.introducerRewardRatio !== null &&
          item.introducerRewardRatio !== undefined &&
          item.introducerRewardRatio !== '')
      );
    });

    if (isSmall) {
      // --- SPサイズの場合の表示 (OldOrderDetailExpandのデザインに近づけた横スクロールFlexレイアウト) ---
      content = (
        <Box sx={{ width: 'fit-content' }}>
          {/* (A) パートナー報酬セクション */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', pb: 1, mb: 1 }}>
            <Typography sx={{ flex: 2, pr: 1,  }}>
              {translate('resources.orders.fields.productId')}
            </Typography>
            <Typography sx={{ flex: 3, pr: 1,  }}>
              {translate('resources.orders.fields.productName')}
            </Typography>
            <Typography sx={{ flex: 1, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.quantity')}
            </Typography>
            <Typography sx={{ flex: 2, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.productSubtotal')}
            </Typography>
            <Typography sx={{ flex: 1.2, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.isExclude')}
            </Typography>
            <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.basePartnerReward')}
            </Typography>
            <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.campaignRewardDelta')}
            </Typography>
            <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.usageCountRewardDelta')}
            </Typography>
            <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>
              {translate('resources.orders.fields.existingCustomerRewardDelta')}
            </Typography>
            <Typography sx={{ flex: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', pr: 1 }}>
              {translate('resources.orders.fields.productReward')}
              <Tooltip
                title="※購入者ID（会員ID）の提供がない場合やIDに個人情報が含まれる場合は、「ECサイト顧客ID」は「-」と表示されます。"
                placement="top"
                arrow
              >
                <InfoOutlinedIcon fontSize="small" color="error" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Typography>
            <Typography sx={{ flex: 2, textAlign: 'right' }}>
              {translate('resources.orders.fields.productRewardPrice')}
            </Typography>
          </Box>

          {/* SP用パートナー明細行 */}
          {details.map((item: any, index: number) => {
            // 配分比率（%表現）にパースして整形
            const partnerRatio = item.partnerRewardRatio ? (parseFloat(item.partnerRewardRatio) * 100).toFixed(0) : '';

            // 親子の基本報酬率の逆算
            const childBaseRate = item.reward != null 
              ? parseFloat(item.reward) 
                - (item.childCampaignRewardDelta ? parseFloat(item.childCampaignRewardDelta) : 0)
                - (item.childUsageCountRewardDelta ? parseFloat(item.childUsageCountRewardDelta) : 0)
                - (item.childExistingCustomerRewardDelta ? parseFloat(item.childExistingCustomerRewardDelta) : 0)
              : null;

            // 2次パートナー用内訳がnullの場合は、1次パートナーまたは旧基準のフィールドを表示
            const isFirstTier = partnerRatio == '';

            const displayBaseRate = item.basePartnerReward;
            const displayCampaignDelta = isFirstTier ? item.campaignRewardDelta : item.childCampaignRewardDelta;
            const displayUsageCountDelta = isFirstTier ? item.usageCountRewardDelta : item.childUsageCountRewardDelta;
            const displayExistingCustomerDelta = isFirstTier ? item.existingCustomerRewardDelta : item.childExistingCustomerRewardDelta;

            return (
              <Box key={`${item.id || index}-child-sp`} sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', py: 1.5 }}>
                <Typography sx={{ flex: 2, pr: 1, wordBreak: 'break-all' }}>{item.productId}</Typography>
                <Typography sx={{ flex: 3, pr: 1 }}>
                  {item.productName && item.productName.length >= 15
                    ? `${item.productName.substring(0, 15)}...`
                    : item.productName}
                </Typography>
                <Typography sx={{ flex: 1, textAlign: 'right', pr: 1 }}>{item.quantity?.toLocaleString()}</Typography>
                <Typography sx={{ flex: 2, textAlign: 'right', pr: 1 }}>{item.productPrice?.toLocaleString()}円</Typography>
                <Typography sx={{ flex: 1.2, textAlign: 'right', pr: 1 }}>{formatIsExclude(item.isExclude)}</Typography>
                <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(displayBaseRate)}</Typography>
                <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}> {formatBaseRate(displayCampaignDelta)}</Typography>
                <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(displayUsageCountDelta)}</Typography>
                <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(displayExistingCustomerDelta)}</Typography>
                <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{item.productName === '割引' ? '' : item.reward != null ? parseFloat(item.reward).toLocaleString() + '%' : ''}</Typography>
                <Typography sx={{ flex: 2, textAlign: 'right' }}>{item.rewardPrice != null ? Math.floor(parseFloat(item.rewardPrice)).toLocaleString() + '円' : ''}</Typography>
              </Box>
            );
          })}

          {/* (B) 1次パートナー（親）紹介者報酬セクション */}
          {orderHasIntroducer && !hideParentReward && (
            <>
              <Box sx={{  py: 1, px: 1, mt: 3, mb: 1.5, borderRadius: 1 }}>
                <Typography>
                  1次パートナー: {parentPartnerName || ''}
                </Typography>
              </Box>
              {/* SP用紹介者明細行 */}
              {details.map((item: any, index: number) => {
                const introducerPrice = item.introducerRewardPrice !== null ? parseFloat(item.introducerRewardPrice) : 0;
                const isBaseIntroducerRewardNull = item.baseIntroducerReward === null || item.baseIntroducerReward === undefined || item.baseIntroducerReward === '';

                if (introducerPrice === 0 && isBaseIntroducerRewardNull) {
                  return null;
                }

                const hasIntroducer =
                  introducerPrice > 0 ||
                  (item.introducerRewardRatio !== null &&
                    item.introducerRewardRatio !== undefined &&
                    item.introducerRewardRatio !== '');

                if (!hasIntroducer) return null;

                return (
                  <Box key={`${item.id || index}-parent-sp`} sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', py: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.015)' }}>
                    <Typography sx={{ flex: 2, pr: 1, wordBreak: 'break-all' }}>{item.productId}</Typography>
                    <Typography sx={{ flex: 3, pr: 1 }}>
                      {item.productName && item.productName.length >= 15
                        ? `${item.productName.substring(0, 15)}...`
                        : item.productName}
                    </Typography>
                    <Typography sx={{ flex: 1, textAlign: 'right', pr: 1 }}>{item.quantity?.toLocaleString()}</Typography>
                    <Typography sx={{ flex: 2, textAlign: 'right', pr: 1 }}>{item.productPrice?.toLocaleString()}円</Typography>
                    <Typography sx={{ flex: 1.2, textAlign: 'right', pr: 1 }}>{formatIsExclude(item.isExclude)}</Typography>
                    <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(item.baseIntroducerReward)}</Typography>
                    <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(item.parentCampaignRewardDelta)}</Typography>
                    <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(item.parentUsageCountRewardDelta)}</Typography>
                    <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{formatBaseRate(item.parentExistingCustomerRewardDelta)}</Typography>
                    <Typography sx={{ flex: 1.5, textAlign: 'right', pr: 1 }}>{item.introducerReward != null ? parseFloat(item.introducerReward).toLocaleString() + '%' : ''}</Typography>
                    <Typography sx={{ flex: 2, textAlign: 'right' }}>{item.introducerRewardPrice != null ? Math.floor(parseFloat(item.introducerRewardPrice)).toLocaleString() + '円' : ''}</Typography>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      );
    } else {
      // --- PCサイズの場合の表示 ---
      content = (
        <Box
        sx={{            // ECサイト顧客ID の右余白を消す
            '& .client-id-column': {
              width: '120px',
              paddingRight: '0px !important',
              textAlign: 'center'
            },

            // ツールチップ の左余白を消す
            '& .tooltip-column': {
              width: '24px',
              paddingLeft: '0px !important'
            }}}>
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '11%', wordBreak: 'break-all' }}>
                  {translate('resources.orders.fields.productId')}
                </TableCell>
                <TableCell sx={{ width: '14%' }}>{translate('resources.orders.fields.productName')}</TableCell>
                <TableCell sx={{ width: '5%' }} align="right">
                  {translate('resources.orders.fields.quantity')}
                </TableCell>
                <TableCell sx={{ width: '9%' }} align="right">
                  {translate('resources.orders.fields.productSubtotal')}
                </TableCell>
                <TableCell sx={{ width: '6%' }} align="right">
                  {translate('resources.orders.fields.isExclude')}
                </TableCell>
                <TableCell sx={{ width: '8%' }} align="right">
                  {translate('resources.orders.fields.basePartnerReward')}
                </TableCell>
                <TableCell sx={{ width: '11%' }} align="right">
                  {translate('resources.orders.fields.campaignRewardDelta')}
                </TableCell>
                <TableCell sx={{ width: '9%' }} align="right">
                  {translate('resources.orders.fields.usageCountRewardDelta')}
                </TableCell>
                <TableCell sx={{ width: '9%' }} align="right">
                  {translate('resources.orders.fields.existingCustomerRewardDelta')}
                </TableCell>
                <TableCell sx={{ width: '8%' }} align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                    {translate('resources.orders.fields.productReward')}
                    <Tooltip
                      title="※システム内部では、各報酬率を小数第4桁まで保持しているため、合計報酬率と一致しないことがあります"
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon fontSize="small" color="error" sx={{ cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: '10%' }} align="right">
                  {translate('resources.orders.fields.productRewardPrice')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* --- パートナー報酬明細のループ --- */}
              {details.map((item: any, index: number) => {
                const introducerPrice = item.introducerRewardPrice !== null ? parseFloat(item.introducerRewardPrice) : 0;
                const hasIntroducer =
                  introducerPrice > 0 ||
                  (item.introducerRewardRatio !== null &&
                    item.introducerRewardRatio !== undefined &&
                    item.introducerRewardRatio !== '');

                // 親子の基本報酬率の逆算
                const childBaseRate = item.reward != null 
                  ? parseFloat(item.reward) 
                    - (item.childCampaignRewardDelta ? parseFloat(item.childCampaignRewardDelta) : 0)
                    - (item.childUsageCountRewardDelta ? parseFloat(item.childUsageCountRewardDelta) : 0)
                    - (item.childExistingCustomerRewardDelta ? parseFloat(item.childExistingCustomerRewardDelta) : 0)
                  : null;

                // 2次パートナー用内訳がnullの場合は、1次パートナーまたは旧基準のフィールドを表示
                const partnerRatio = item.partnerRewardRatio ? (parseFloat(item.partnerRewardRatio) * 100).toFixed(0) : '';
                const isFirstTier = partnerRatio == '';

                const displayBaseRate = item.basePartnerReward;
                const displayCampaignDelta = isFirstTier ? item.campaignRewardDelta : item.childCampaignRewardDelta;
                const displayUsageCountDelta = isFirstTier ? item.usageCountRewardDelta : item.childUsageCountRewardDelta;
                const displayExistingCustomerDelta = isFirstTier ? item.existingCustomerRewardDelta : item.childExistingCustomerRewardDelta;

                return (
                  <TableRow key={`${item.id || index}-child-pc`}>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{item.productId}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item.productName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{item.quantity?.toLocaleString()}</TableCell>
                    <TableCell align="right">{item.productPrice?.toLocaleString()}円</TableCell>
                    <TableCell align="right">{formatIsExclude(item.isExclude)}</TableCell>
                    
                    {/* 2次パートナー按分基本報酬率 */}
                    <TableCell align="right">{formatBaseRate(displayBaseRate)}</TableCell>
                    {/* 2次パートナー按分キャンペーン率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(displayCampaignDelta)}</TableCell>
                    {/* 2次パートナー按分購入回数加算率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(displayUsageCountDelta)}</TableCell>
                    {/* 2次パートナー按分既存顧客減算率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(displayExistingCustomerDelta)}</TableCell>
                    {/* 最終適用率 (2次最終) */}
                    <TableCell align="right">{item.productName === '割引' ? '' : item.reward != null ? parseFloat(item.reward).toLocaleString() + '%' : ''}</TableCell>
                    {/* 2次報酬金額 */}
                    <TableCell align="right">{item.rewardPrice != null ? Math.floor(parseFloat(item.rewardPrice)).toLocaleString() + '円' : ''}</TableCell>
                  </TableRow>
                );
              })}

              {/* --- 紹介者報酬セクションの見出し --- */}
              {orderHasIntroducer && !hideParentReward && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 1 }}>
                    1次パートナー: {parentPartnerName || ''}
                  </TableCell>
                </TableRow>
              )}

              {/* --- (4) 紹介者（親）報酬明細のループ --- */}
              {orderHasIntroducer && !hideParentReward && details.map((item: any, index: number) => {
                const introducerPrice = item.introducerRewardPrice !== null ? parseFloat(item.introducerRewardPrice) : 0;
                const hasIntroducer =
                  introducerPrice > 0 ||
                  (item.introducerRewardRatio !== null &&
                    item.introducerRewardRatio !== undefined &&
                    item.introducerRewardRatio !== '');

                if (!hasIntroducer) return null;

                return (
                  <TableRow 
                    key={`${item.id || index}-parent-pc`}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.015)' }} // 識別しやすいよう若干背景を調整
                  >
                    <TableCell sx={{ pl: 3 }}>{item.productId}</TableCell>
                    <TableCell sx={{ pl: 2 }}>
                      {item.productName}
                    </TableCell>
                    <TableCell align="right">{item.quantity?.toLocaleString()}</TableCell>
                    <TableCell align="right">{item.productPrice?.toLocaleString()}円</TableCell>
                    <TableCell align="right">{formatIsExclude(item.isExclude)}</TableCell>
                    
                    {/* 親の基本紹介報酬率 */}
                    <TableCell align="right">{formatBaseRate(item.baseIntroducerReward)}</TableCell>
                    {/* 親の按分キャンペーン率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(item.parentCampaignRewardDelta)}</TableCell>
                    {/* 親の按分購入回数紹介率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(item.parentUsageCountRewardDelta)}</TableCell>
                    {/* 親の按分既存顧客紹介減算率 + 比率表示 */}
                    <TableCell align="right">{formatBaseRate(item.parentExistingCustomerRewardDelta)}</TableCell>
                    {/* 親の最終適用率 */}
                    <TableCell align="right">{item.introducerReward != null ? parseFloat(item.introducerReward).toLocaleString() + '%' : ''}</TableCell>
                    {/* 親の紹介報酬金額 */}
                    <TableCell align="right">{item.introducerRewardPrice != null ? Math.floor(parseFloat(item.introducerRewardPrice)).toLocaleString() + '円' : ''}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      );
    }
  }

  // 外側のセルで「親テーブルのセルの余白」と「背景色」を設定する
  return (
    <TableCell
      colSpan={16}
      sx={{
        padding: '12px 16px', // 親セルのパディングに合わせる
        backgroundColor: '#f9f9f9', // 背景色を設定
        border: 'none'
      }}
    >
      {content}
    </TableCell>
  );
};
export default OrderDetailExpand;
